import { useState } from 'react';

import {
  GetApplicationResponse,
  GetFormalOffersResponse,
} from '@iwoca/lapi-client/edge';
import { GetBuyerSpendingLimitResponse } from '@iwoca/lapi-client/iwocapay';
import { Button, Card } from '@iwoca/orion';
import { useQueries } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { sum } from 'lodash-es';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router';

import {
  useGetApplication,
  useGetBuyerSpendingLimit,
  useGetFormalOffers,
} from '../../../../api/lending/lapiHooks';
import { useAccountNavigation } from '../../../../hooks/useAccountNavigation';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { displayCurrency } from '../../../../utils/DisplayCurrency';
import {
  ExampleRepaymentScheduleOverview,
  getExampleRepaymentScheduleOverview,
} from '../../../../utils/exampleRepaymentSchedule';
import { roundTo } from '../../../../utils/roundTo';
import { useThreatMetrixProfiling } from '../../../../utils/threatMetrix/ThreatMetrixProfiling';
import { trackSelectedOffer } from '../../../../utils/tracking';
import { useCurrentPayLink } from '../../../Checkout/hooks/useCurrentPayLink';
import { CHECKOUT_BASE_PATH } from '../../../Checkout/routes';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useTrackViewedOffers } from '../../../hooks/useTrackViewedOffers';
import {
  PayLink,
  TDuration,
} from '../../../PayLinkLanding/utils/PayLinkLanding.types';
import {
  getDurationInMonths,
  productFromDuration,
} from '../../../utils/product';
import { buildQueryString } from '../../../utils/queryParams';

export const ChooseProduct = () => {
  const { navigateToAccount } = useAccountNavigation();
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
  const { payLink, loadingPayLink } = useCurrentPayLink();
  const { application } = useGetApplication();
  const { spendingLimit, loadingSpendingLimit } = useGetBuyerSpendingLimit();
  const results = useQueries({
    queries: useGetMultiOfferDrawDownRepaymentScheduleConfig({ formalOffers }),
  });
  const [activeScheduleId, setActiveScheduleId] = useState<string | null>(null);
  const navigate = useNavigate();

  const { isMobile } = useIsMobile();

  useTrackViewedOffers({
    payLinkId: payLink?.id,
    formalOffers: formalOffers?.formal_offers,
    loadingApis: loadingPayLink || loadingFormalOffers,
  });

  useThreatMetrixProfiling(payLink?.seller_handle, payLink?.id);

  if (
    loadingFormalOffers ||
    results.some(({ isLoading, isPending }) => isLoading || isPending) ||
    !formalOffers?.formal_offers ||
    !application ||
    loadingSpendingLimit
  )
    return <ChooseProductLoadingPlaceholder />;

  const sellerEnabled12m = hasSellerEnabled12m({ payLink });
  const sellerEnabled30d = hasSellerEnabled30d({ payLink });

  const offers = formalOffers.formal_offers.filter(
    (offer) => offer.duration && getDurationInMonths(offer.duration) === 3,
  );
  const offer12m = formalOffers.formal_offers.find(
    (offer) => offer.duration && getDurationInMonths(offer.duration) === 12,
  );
  if (sellerEnabled12m && offer12m) {
    offers.push(offer12m);
  }
  const offer30d = formalOffers.formal_offers.find(
    (offer) => offer.duration && getDurationInMonths(offer.duration) === 1,
  );
  if (sellerEnabled30d && offer30d) {
    offers.push(offer30d);
  }

  const availableOffers = filterOutPartialOffers({
    offers,
    payLink,
    spendingLimit: spendingLimit?.data?.spending_limits,
  });

  const isPartiallyApproved = availableOffers.length === 0;

  const hasMultipleOffers = availableOffers.length > 1;

  const repaymentSchedules = results.map(({ data }) => data!);
  const repaymentSchedule =
    repaymentSchedules?.find(
      (schedule) => schedule?.schedule_id === activeScheduleId,
    ) || null;
  const productData = getProductData({
    offers: availableOffers,
    repaymentSchedules: repaymentSchedules,
    application,
  });

  const getFormattedInterestRate = (
    duration: TDuration,
    interestRate: number,
  ) => {
    const productPricing = payLink?.product_pricing;
    const productLabel = productFromDuration[duration];
    const isInterestFree =
      productPricing?.[productLabel]?.representative_interest === 0;

    if (isInterestFree) {
      return 'Interest free';
    }
    const formattedInterestRate = roundTo(interestRate * 100, 2);
    return `${formattedInterestRate}%`;
  };

  const shouldSkipToCheckout = () => {
    // If 3m is the only enabled product, then they must have selected 3m on the landing page.
    // If they're approved (and therefore on this page) they have a 3m offer (true for now).
    // Given the above, we can skip to checkout, as they have no choice to make on this page.
    // Unless interest bearing, in which case we need to show them the non-rep rate offer for compliance reasons.
    return (
      only3mEnabled({ payLink }) &&
      payLink?.product_pricing?.THREE_MONTHS?.representative_interest === 0
    );
  };

  const skipToCheckout = () => {
    const offer_3m = availableOffers.find((offer) => {
      if (!offer.duration) return false;
      return getDurationInMonths(offer.duration) === 3;
    });
    if (!offer_3m?.offer_id) return;

    const queryString = buildQueryString({ offerId: offer_3m.offer_id }, true);
    navigate(`/pay/checkout/${queryString}`);
  };

  const goToCheckout = ({ offerId }: { offerId: string }) => {
    trackSelectedOffer({
      payLinkId: payLink!.id,
      offerIds: productData.map((offer) => offer.id!),
      offerId,
    });

    const queryString = buildQueryString({ offerId }, true);
    // eslint-disable-next-line react-hooks/immutability -- intentional full-page navigation to checkout
    window.location.href = `${CHECKOUT_BASE_PATH}/${queryString}`;
  };

  // If partial, send to pandora
  if (isPartiallyApproved) {
    navigateToAccount(buildQueryString());
  }

  if (shouldSkipToCheckout()) {
    skipToCheckout();
  }

  return (
    <div>
      <div className="mb-5xl text-center">
        {!sellerEnabled12m ? (
          <>
            <div className="mb-l text-3xl font-bold text-success-drk">
              Approved
            </div>
          </>
        ) : hasMultipleOffers ? (
          <>
            <div className="mb-l text-3xl font-bold">
              Good news! You have a choice
            </div>
            <p className="!mb-l text-xl font-med">
              You have been approved and have{' '}
              {formalOffers.formal_offers.length} options available.
            </p>
            <p className="text-m font-reg">Choose an option to continue</p>
          </>
        ) : (
          <>
            <h1 className="mb-l text-3xl font-bold">
              Sorry, we can't approve you for
              <br />
              12 months right now
            </h1>
            <p className="!mb-l text-xl font-med">
              But you can still pay over 90 days instead.
            </p>
          </>
        )}
      </div>
      {isMobile ? (
        <MultipleOffersCards
          sellerEnabled12m={sellerEnabled12m}
          productData={productData}
          repaymentSchedule={repaymentSchedule}
          activeScheduleId={activeScheduleId}
          goToCheckout={goToCheckout}
          setActiveScheduleId={setActiveScheduleId}
          getFormattedInterestRate={getFormattedInterestRate}
        />
      ) : (
        <MultipleOffersTable
          sellerEnabled12m={sellerEnabled12m}
          hasMultipleOffers={hasMultipleOffers}
          productData={productData}
          repaymentSchedule={repaymentSchedule}
          activeScheduleId={activeScheduleId}
          goToCheckout={goToCheckout}
          setActiveScheduleId={setActiveScheduleId}
          getFormattedInterestRate={getFormattedInterestRate}
        />
      )}
    </div>
  );
};

const CardRow = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={clsx(
        'flex w-full flex-wrap justify-between border-b border-secondary-80 p-l',
        className,
      )}
    >
      {children}
    </div>
  );
};

const MultipleOffersCards = ({
  sellerEnabled12m,
  productData,
  repaymentSchedule,
  activeScheduleId,
  goToCheckout,
  setActiveScheduleId,
  getFormattedInterestRate,
}: {
  sellerEnabled12m: boolean;
  productData: ReturnType<typeof getProductData>;
  repaymentSchedule: ExampleRepaymentScheduleOverview | null;
  activeScheduleId: string | null;
  goToCheckout: ({ offerId }: { offerId: string }) => void;
  setActiveScheduleId: (id: string | null) => void;
  getFormattedInterestRate: (
    duration: TDuration,
    interestRate: number,
  ) => string;
}) => {
  return productData.map(
    ({
      id,
      duration,
      totalRepayment,
      interestRate,
      name,
      schedule_id,
      originalChoice,
    }) => {
      const isScheduleVisible = schedule_id === activeScheduleId;
      return (
        <Card
          key={id}
          className="relative mx-m mb-3xl flex !w-[calc(100%-24px)] flex-col !p-0"
        >
          {originalChoice && sellerEnabled12m && (
            <div className="absolute left-l right-l top-0 -translate-y-full rounded-t-m bg-success-drk px-l py-s text-s font-bold text-white">
              Your original choice
            </div>
          )}
          <div className="flex w-full justify-center border-b border-secondary-80 p-l text-xl font-bold">
            Pay in
            {duration === 1 ? ' 30 days' : ` ${duration} monthly instalments`}
          </div>
          <CardRow>
            <div className="text-l font-med">Repayment</div>
            <div className="flex flex-col">
              <span className="font-bold">
                {displayCurrency(totalRepayment)}
              </span>
              <span className="text-s font-reg">per month</span>
            </div>
          </CardRow>
          <CardRow>
            <div className="text-l font-med">Duration</div>
            <div className="flex flex-col items-end">
              <span className="font-bold">
                {duration === 1 ? '30 days' : `${duration} months`}
              </span>
              <span className="text-s font-reg">It’s free to repay early!</span>
            </div>
          </CardRow>
          <CardRow>
            <div className="text-l font-med">Interest rate</div>
            <div className="font-bold">
              {getFormattedInterestRate(duration, interestRate)}
            </div>
          </CardRow>
          <CardRow>
            <div className="text-l font-med">Total repayment</div>
            <div className="font-bold">{displayCurrency(totalRepayment)}</div>
            <div className="w-full text-s font-reg">
              Reduce this by making early repayments for free
            </div>
          </CardRow>
          <div className="w-full rounded-b-l bg-primary-97 p-m">
            <WhiteLabelOverride componentType="button" disabled={false}>
              <Button
                className="w-full"
                onClick={() => goToCheckout({ offerId: id! })}
              >
                Check out with {name}
              </Button>
            </WhiteLabelOverride>
            <Button
              className="w-full"
              variant="tertiary"
              onClick={() => {
                if (isScheduleVisible) {
                  setActiveScheduleId(null);
                  return;
                }

                setActiveScheduleId(schedule_id);
              }}
            >
              {!isScheduleVisible ? 'View schedule +' : 'Hide schedule -'}
            </Button>
            {isScheduleVisible && repaymentSchedule !== null && (
              <BreakdownTable repaymentSchedule={repaymentSchedule} />
            )}
          </div>
        </Card>
      );
    },
  );
};

const MultipleOffersTable = ({
  sellerEnabled12m,
  hasMultipleOffers,
  productData,
  repaymentSchedule,
  activeScheduleId,
  goToCheckout,
  setActiveScheduleId,
  getFormattedInterestRate,
}: {
  sellerEnabled12m: boolean;
  hasMultipleOffers: boolean;
  productData: ReturnType<typeof getProductData>;
  repaymentSchedule: ExampleRepaymentScheduleOverview | null;
  activeScheduleId: string | null;
  goToCheckout: ({ offerId }: { offerId: string }) => void;
  setActiveScheduleId: (id: string | null) => void;
  getFormattedInterestRate: (
    duration: TDuration,
    interestRate: number,
  ) => string;
}) => {
  return (
    <>
      <div>
        <table className="mx-auto w-full border-spacing-0 rounded-m border border-structure-80 bg-structure-100 shadow-black-s">
          <tbody>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
              <th className="flex flex-col justify-center border-b border-structure-80 p-xl text-left text-m font-med">
                Monthly repayment
              </th>
              {productData.map(({ id, monthlyRepayment, originalChoice }) => (
                <td
                  key={id}
                  className="relative flex flex-col justify-center border-b border-structure-80 p-xl text-center text-xl font-med"
                >
                  {originalChoice && sellerEnabled12m && (
                    <div className="absolute left-l right-l top-0 -translate-y-full rounded-t-m bg-success-drk px-l py-s text-s font-bold text-white">
                      Your original choice
                    </div>
                  )}
                  <span>
                    {displayCurrency(monthlyRepayment)}
                    <span className="font-reg">/mo</span>
                  </span>
                </td>
              ))}
            </tr>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
              <th className="flex flex-col justify-center border-b border-structure-80 p-xl text-left text-m font-med">
                Duration
                <p className="!m-0 text-xs font-reg">
                  It’s free to repay early!
                </p>
              </th>
              {productData.map(({ id, duration }) => (
                <td
                  key={id}
                  className="flex flex-col justify-center border-b border-structure-80 p-xl text-center text-xl font-med"
                >
                  {duration === 1 ? '30 days' : `${duration} months`}
                </td>
              ))}
            </tr>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
              <th className="flex flex-col justify-center border-b border-structure-80 p-xl text-left text-m font-med">
                Interest rate
              </th>
              {productData.map(({ duration, interestRate }) => {
                return (
                  <td
                    key={`interestRate_${duration}`}
                    className="flex flex-col justify-center border-b border-structure-80 p-xl text-center text-xl font-med"
                  >
                    {getFormattedInterestRate(duration, interestRate)}
                  </td>
                );
              })}
            </tr>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]">
              <th className="flex flex-col justify-center border-b border-structure-80 p-xl text-left text-m font-med">
                Total repayment
                <p className="!m-0 text-xs font-reg">
                  Reduce this by making early repayments for free
                </p>
              </th>
              {productData.map(({ totalRepayment }) => (
                <td
                  key={totalRepayment}
                  className="flex flex-col justify-center border-b border-structure-80 p-xl text-center text-xl font-med"
                >
                  {displayCurrency(totalRepayment)}
                </td>
              ))}
            </tr>
          </tbody>
          <tfoot>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))] overflow-hidden rounded-b-m bg-secondary-97">
              {hasMultipleOffers ? (
                <th className="flex flex-col justify-center p-xl text-left text-m font-med"></th>
              ) : null}
              {productData.map(({ id, name, schedule_id }) => {
                const isScheduleVisible = schedule_id === activeScheduleId;

                return (
                  <td
                    key={name}
                    className="flex flex-col justify-center p-xl text-center text-xl font-med"
                  >
                    <WhiteLabelOverride componentType="button" disabled={false}>
                      <Button
                        className="w-full !text-s font-bold"
                        onClick={() => goToCheckout({ offerId: id! })}
                      >
                        Check out with {hasMultipleOffers ? <br /> : null}
                        {name}
                      </Button>
                    </WhiteLabelOverride>
                    <Button
                      className="!text-s font-bold"
                      variant="tertiary"
                      onClick={() => {
                        if (isScheduleVisible) {
                          setActiveScheduleId(null);
                          return;
                        }

                        setActiveScheduleId(schedule_id);
                      }}
                    >
                      {!isScheduleVisible
                        ? 'View schedule +'
                        : 'Hide schedule -'}
                    </Button>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      {repaymentSchedule !== null && (
        <BreakdownTable repaymentSchedule={repaymentSchedule} />
      )}
    </>
  );
};

const ChooseProductLoadingPlaceholder = () => {
  // Fill with placeholder data
  const columns = new Array(2).fill(0);
  const rows = new Array(3).fill(0);

  return (
    <div data-testid="choose-product-loading-skeleton">
      <div className="mb-5xl text-center">
        <>
          <h1 className="mb-l text-3xl font-bold">
            <Skeleton height="44px" width="80%" />
          </h1>
          <p className="!mb-l text-xl font-med">
            <Skeleton height="28px" />
          </p>
          <p className="text-m font-reg">
            <Skeleton height="24px" width="30%" />
          </p>
        </>
      </div>
      <div>
        <table className="mx-auto w-full border-spacing-0 rounded-m border border-structure-80 bg-structure-100 shadow-black-s">
          <tbody>
            {rows.map((_, i) => (
              <tr
                key={i}
                className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]"
              >
                <th className="flex flex-col justify-center border-b border-structure-80 p-xl text-left text-m font-med">
                  <Skeleton height="44px" width="60%" />
                </th>
                {columns.map((_, j) => (
                  <td
                    key={`${i}_${j}`}
                    className="flex flex-col justify-center border-b border-structure-80 p-xl text-center text-xl font-med"
                  >
                    <Skeleton width="80%" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="grid grid-cols-[repeat(auto-fit,minmax(0,1fr))] overflow-hidden rounded-b-m bg-secondary-97">
              <th className="flex flex-col justify-center p-xl text-left text-m font-med"></th>
              {columns.map((_, i) => {
                return (
                  <td
                    key={i}
                    className="flex flex-col justify-center p-xl text-center text-xl font-med"
                  >
                    <Button
                      className="w-full text-s font-bold"
                      onClick={() => {}}
                      disabled={true}
                    >
                      <Skeleton width="100%" />
                    </Button>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const BreakdownTable = ({
  repaymentSchedule,
}: {
  repaymentSchedule: ExampleRepaymentScheduleOverview;
}) => {
  const tableHeaders = ['Date', 'Invoice amount', 'Interest*', 'Total'];
  const tableData = repaymentSchedule.fullSchedule.map(
    ({ date, principal, interest, total }) => {
      return [
        formatDate(date),
        displayCurrency(principal),
        displayCurrency(interest),
        displayCurrency(total),
      ];
    },
  );

  const invoiceTotal = sum(
    repaymentSchedule.fullSchedule.map(({ principal }) => principal),
  );

  const interestTotal = sum(
    repaymentSchedule.fullSchedule.map(({ interest }) => interest),
  );

  const totalTotal = sum(
    repaymentSchedule.fullSchedule.map(({ total }) => total),
  );

  return (
    <div className="mt-xs overflow-hidden rounded-m border border-secondary-80 bg-structure-100 max-600:overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {tableHeaders.map((title) => (
              <th
                className="border-b border-secondary-80 bg-secondary-97 px-l py-m first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']"
                key={title}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row) => (
            <tr key={row.join('_')}>
              {row.map((data) => (
                <td
                  className="bg-structure-100 px-m py-s text-center font-reg first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right last:font-bold max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']"
                  key={data}
                >
                  {data}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="border-t border-secondary-80 bg-secondary-97 px-m py-s text-center font-reg first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right last:font-bold max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']">
              <b>Total</b>
            </td>
            <td className="border-t border-secondary-80 bg-secondary-97 px-m py-s text-center font-reg first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right last:font-bold max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']">
              {displayCurrency(invoiceTotal)}
            </td>
            <td className="border-t border-secondary-80 bg-secondary-97 px-m py-s text-center font-reg first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right last:font-bold max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']">
              {displayCurrency(interestTotal)}
            </td>
            <td className="border-t border-secondary-80 bg-secondary-97 px-m py-s text-center font-reg first:sticky first:left-0 first:text-left first:shadow-black-l last:text-right last:font-bold max-600:first:after:absolute max-600:first:after:right-[-1px] max-600:first:after:top-0 max-600:first:after:h-full max-600:first:after:w-px max-600:first:after:bg-secondary-80 max-600:first:after:content-['']">
              {displayCurrency(totalTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

function formatDate(rawDate: string) {
  return rawDate.split('-').reverse().join('.');
}

function getProductData({
  offers,
  repaymentSchedules,
  application,
}: {
  offers: NonNullable<
    NonNullable<GetFormalOffersResponse['data']>['formal_offers']
  >;
  repaymentSchedules: Awaited<
    ReturnType<typeof getExampleRepaymentScheduleOverview>
  >[];
  application: GetApplicationResponse['data'];
}) {
  if (!offers) return [];

  const requestedProduct = application?.requested_products?.[0];
  if (!requestedProduct) return [];

  // TODO(types): fix in schema / add null checks.
  const applicationDuration = getDurationInMonths(requestedProduct.duration!);

  const productOfferData = offers.map(
    ({ offer_id, interest_rates, duration: offerDuration }) => {
      const duration = getDurationInMonths(offerDuration!);
      const product = productFromDuration[duration as TDuration];
      const schedule = repaymentSchedules.find(
        (schedule) => schedule?.product === product,
      )!;
      const rate = interest_rates?.[0].rate;

      return {
        id: offer_id,
        schedule_id: schedule?.schedule_id,
        name: duration === 1 ? '30 days' : `${duration} months`,
        monthlyRepayment: schedule?.exampleMonthlyRepayment,
        interestRate: rate!,
        duration: duration as TDuration,
        totalRepayment: schedule?.exampleTotalRepayment,
        originalChoice: applicationDuration === duration,
      };
    },
  );

  return productOfferData;
}

function useGetMultiOfferDrawDownRepaymentScheduleConfig({
  formalOffers,
}: {
  formalOffers: NonNullable<GetFormalOffersResponse['data']> | null;
}) {
  const payLink = useCurrentPayLink();
  const offers = formalOffers?.formal_offers;
  if (!offers || !payLink) return [];

  return offers.map((offer) => {
    const { duration: offerDuration } = offer;

    const rate = offer.interest_rates?.[0].rate;
    const duration = getDurationInMonths(offerDuration!) as TDuration;

    const productPricing = payLink?.payLink?.product_pricing;
    const productLabel = productFromDuration[duration];
    const isInterestFree =
      productPricing?.[productLabel]?.representative_interest === 0;

    const representativeInterest = isInterestFree ? 0 : rate!;

    const pricingPromotions =
      payLink.payLink?.product_pricing?.[productLabel]?.promotions!;
    const requestedAmount = payLink.payLink?.amount;

    return {
      queryKey: [
        'fetchGetExampleRepaymentSchedule',
        requestedAmount,
        duration,
        representativeInterest,
        pricingPromotions,
      ],
      queryFn: () =>
        getExampleRepaymentScheduleOverview(
          requestedAmount!,
          representativeInterest,
          pricingPromotions,
          duration,
        ),
      enabled: Boolean(requestedAmount) && Boolean(duration),
    };
  });
}

const hasSellerEnabled12m = ({ payLink }: { payLink?: PayLink }) => {
  if (!payLink?.product_pricing) return false;
  return Object.keys(payLink?.product_pricing).includes('TWELVE_MONTHS');
};

const hasSellerEnabled30d = ({ payLink }: { payLink?: PayLink }) => {
  if (!payLink?.product_pricing) return false;
  return Object.keys(payLink?.product_pricing).includes('THIRTY_DAYS');
};

const only3mEnabled = ({ payLink }: { payLink?: PayLink }) => {
  if (!payLink?.product_pricing) return false;
  const productPricing = payLink.product_pricing;
  return (
    Object.keys(productPricing).length === 1 &&
    Object.keys(productPricing)[0] === 'THREE_MONTHS'
  );
};

const filterOutPartialOffers = ({
  offers,
  payLink,
  spendingLimit,
}: {
  offers: GetFormalOffersResponse['data']['formal_offers'];
  payLink?: PayLink;
  spendingLimit?: GetBuyerSpendingLimitResponse['data']['spending_limits'];
}) => {
  const payLinkAmount = payLink?.amount || 0;

  return offers.filter((offer) => {
    if (!offer.duration) return false;

    // We are using spending limit amount instead of offer amount here
    // because the former takes into account pending captured payments
    const durationInMonths = getDurationInMonths(offer.duration);
    const spendingLimitAmount =
      spendingLimit?.find(
        (limit) => limit?.duration_in_months === durationInMonths,
      )?.available_amount || 0;

    return spendingLimitAmount >= payLinkAmount;
  });
};
